import { Breadcrumbs, Button, ConfirmDeleteDialog, FormattedDate, Navbar, OverlayContent, SimpleIcon, Skeleton } from '../../../components'
import { ChatPageOptionsMenu } from './ChatPageOptionsMenu'
import { formatPhoneNumber } from '../../../helpers'
import { GetChatByIdResult } from '@indigohive/cogfy-types/endpoints/getChatById'
import { Link, useNavigate } from 'react-router-dom'
import { MessagesSquareIcon, ChevronLeftIcon, CalendarClock, Ellipsis } from 'lucide-react'
import { siWhatsapp } from 'simple-icons'
import { useCogfy, usePermissions, useToasts } from '../../../hooks'
import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { useMutation } from '@tanstack/react-query'

export type ChatPageNavbarProps = {
  workspaceSlug: string
  chat?: GetChatByIdResult
  loading?: boolean
}

export function ChatPageNavbar (props: ChatPageNavbarProps) {
  const { workspaceSlug, loading, chat } = props

  const [chatOptionsEl, setChatOptionsEl] = useState<HTMLElement | null>(null)
  const [deleteChatDialogOpen, setDeleteChatDialogOpen] = useState(Boolean)
  const chatOptionsRef = useRef<HTMLDivElement | null>(null)

  const collectionId = chat?.collectionId
  const isWhatsApp = chat?.provider === 'whatsapp'

  const permissions = usePermissions()
  const isAdmin = permissions.isAdmin

  const { t } = useTranslation()
  const navigate = useNavigate()
  const cogfy = useCogfy()
  const toasts = useToasts()

  const deleteChat = useMutation({
    mutationFn: () => cogfy.deleteChat({ chatId: chat!.id }),
    onSuccess: () => {
      toasts.success(t('chatPage:Chat deleted successfully'))
      navigate(`/${workspaceSlug}/${collectionId}`)
    },
    onError: () => toasts.error(t('chatPage:Failed to delete chat'))
  })

  return (
    <>
      <Navbar>
        <div className="flex flex-row justify-between w-full">
          <div className="flex justify-center items-center">
            {collectionId && (
              <Link
                to={`/${workspaceSlug}/${collectionId}`}
              >
                <ChevronLeftIcon size={20} />
              </Link>
            )}
            <div
              className={clsx(
                'mx-2',
                'rounded-full',
                'w-8',
                'h-8',
                'flex',
                'justify-center',
                'items-center',
                isWhatsApp ? 'bg-[#E5FBE5]' : 'bg-[#E5F2FB]'
              )}
            >
              {isWhatsApp ? <SimpleIcon icon={siWhatsapp} size={16} /> : <MessagesSquareIcon size={16} />}
            </div>
            <Breadcrumbs
              items={[
                {
                  label: loading ? <Skeleton className="w-32 h-4" /> : <ChatBreadcrumb {...props} />
                }
              ]}
            />
          </div>
          <div className="flex gap-2">
            {isWhatsApp && (
              <Button
                to={'scheduled'}
                size="sm"
              >
                <CalendarClock size={16} />
              </Button>
            )}
            {isAdmin && (
              <div ref={chatOptionsRef}>
                <Button
                  ghost
                  size='sm'
                  onClick={() => setChatOptionsEl(chatOptionsRef.current)}
                >
                  <Ellipsis size={16} />
                </Button>
              </div>
            )}
          </div>
        </div>
      </Navbar>

      <OverlayContent
        open={Boolean(chatOptionsEl)}
        onClose={() => setChatOptionsEl(null)}
        anchorEl={chatOptionsEl}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <ChatPageOptionsMenu
          onDeleteChatClick={() => setDeleteChatDialogOpen(true)}
        />
      </OverlayContent>

      <ConfirmDeleteDialog
        title={t('chatPage:Delete chat')}
        open={deleteChatDialogOpen}
        message={t('chatPage:Are you sure you want to delete the chat') + '?'}
        onClose={() => {
          setDeleteChatDialogOpen(false)
          setChatOptionsEl(null)
        }}
        onConfirm={() => deleteChat.mutate()}
      />
    </>
  )
}

function ChatBreadcrumb (props: ChatPageNavbarProps) {
  const { chat } = props

  const whatsAppPhoneNumber = chat?.data?.whatsApp?.contactPhoneNumber
  const contactName = chat?.data?.whatsApp?.contactName

  return (
    <>
      <span className='font-semibold'>
        {whatsAppPhoneNumber
          ? `${contactName ? `${contactName}: ` : ''}${formatPhoneNumber(whatsAppPhoneNumber)}`
          : 'Chat'}
      </span>
      &nbsp;
      <span className='text-gray-600'>
        <FormattedDate date={chat?.createDate} format="Ppp" />
      </span>
    </>
  )
}
